import { debugLog } from './debugLog.js';

/** Collect expense values from dynamic form keys like `automation_Data entry_expense`. */
export function collectPrefixedExpenses(formData, prefix) {
  const expenses = {};
  Object.keys(formData).forEach((key) => {
    if (!key.startsWith(`${prefix}_`) || !key.endsWith('_expense')) return;
    const name = key.slice(prefix.length + 1, -'_expense'.length);
    const raw = formData[key];
    const value =
      typeof raw === 'string'
        ? parseFloat(raw.replace(/[^0-9.-]/g, '')) || 0
        : typeof raw === 'number'
          ? raw
          : 0;
    if (value > 0) expenses[name] = value;
  });
  return expenses;
}

export function parseInexpensiveContractor(value) {
  const result = { hiringContractors: [], expenseFields: {} };
  if (!value) return result;

  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed.contractors)) {
        result.hiringContractors = parsed.contractors;
        if (parsed.expenses && typeof parsed.expenses === 'object') {
          Object.entries(parsed.expenses).forEach(([name, amount]) => {
            result.expenseFields[`hiringContractor_${name}_expense`] = String(amount);
          });
        }
        return result;
      }
    } catch {
      // Fall through to comma-separated parsing
    }
  }

  result.hiringContractors = Array.isArray(value)
    ? value
    : String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  return result;
}

export function formatInexpensiveContractor(hiringContractors, formData) {
  if (!hiringContractors?.length) return null;

  const expenses = collectPrefixedExpenses(formData, 'hiringContractor');
  if (Object.keys(expenses).length > 0) {
    return JSON.stringify({ contractors: hiringContractors, expenses });
  }
  return hiringContractors.join(',');
}

export function parseAIAreas(value) {
  const result = { automationAreas: [], expenseFields: {} };
  if (!value) return result;

  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return result;
    }
  }

  if (Array.isArray(parsed)) {
    result.automationAreas = parsed;
    return result;
  }

  if (parsed && typeof parsed === 'object' && Array.isArray(parsed.areas)) {
    result.automationAreas = parsed.areas;
    if (parsed.expenses && typeof parsed.expenses === 'object') {
      Object.entries(parsed.expenses).forEach(([name, amount]) => {
        result.expenseFields[`automation_${name}_expense`] = String(amount);
      });
    }
  }
  return result;
}

export function formatAIAreas(automationAreas, formData) {
  if (!automationAreas?.length) return null;

  const expenses = collectPrefixedExpenses(formData, 'automation');
  if (Object.keys(expenses).length > 0) {
    return JSON.stringify({ areas: automationAreas, expenses });
  }
  return JSON.stringify(automationAreas);
}

export function extractUnknownFieldName(errorBody) {
  const message = errorBody?.error?.message || '';
  const match = message.match(/"([^"]+)"/);
  return match?.[1] || null;
}

/** Field name from Airtable UNKNOWN_FIELD_NAME or INVALID_VALUE_FOR_COLUMN errors. */
export function extractAirtableFieldNameFromError(errorBody) {
  const message = errorBody?.error?.message || '';
  const quoted = extractUnknownFieldName(errorBody);
  if (quoted && !quoted.includes(' ')) return quoted;

  const cannotParse = message.match(/field\s+([A-Za-z0-9&_?]+)/i);
  if (cannotParse?.[1]) return cannotParse[1];

  const insufficientOption = message.match(/option\s+"([^"]+)"/i);
  if (insufficientOption?.[1]) return null;

  return null;
}

export function describeAirtableSaveError(status, errorBody) {
  const errorType = errorBody?.error?.type || errorBody?.error || '';
  const errorMsg = errorBody?.error?.message || '';

  debugLog('[Save] Airtable error detail:', { status, errorType, errorMsg });

  if (status === 404) {
    return 'Your company profile was not found. A quick refresh usually fixes this.';
  }
  if (status === 401 || status === 403) {
    return 'We could not reach your company profile right now. Your answers are still here — try again in a moment.';
  }

  const unknownField = extractUnknownFieldName(errorBody);
  if (
    errorType === 'UNKNOWN_FIELD_NAME' ||
    String(errorMsg).toLowerCase().includes('unknown field')
  ) {
    return unknownField
      ? `Some optional details (${unknownField}) could not be stored yet. Your other answers are unaffected.`
      : 'A few optional details could not be stored yet. Your main answers are still here.';
  }

  if (
    errorType === 'INVALID_VALUE_FOR_COLUMN' ||
    String(errorMsg).toLowerCase().includes('invalid')
  ) {
    const fieldMatch = errorMsg.match(/"([^"]+)"/) || errorMsg.match(/\(([^)]+)\)/);
    const field = fieldMatch?.[1];
    return field
      ? `One value needs a small adjustment (${field}). Please review it and try again.`
      : 'One of the values on this step needs a small adjustment before it can be saved.';
  }

  if (status === 422) {
    return 'A few details on this step need a quick review before they can be saved.';
  }

  return 'We could not save this step just now. Your answers are still here — try continuing again.';
}

export function getSavePreflightIssues({ baseId, apiKey, companiesTableId, companyId, fields }) {
  if (!apiKey) return { reason: 'config', message: 'Save is temporarily unavailable. Your answers are still here on this page.' };
  if (!baseId || !companiesTableId) return { reason: 'config', message: 'Save is temporarily unavailable. Your answers are still here on this page.' };
  if (!companyId) return { reason: 'no_company', message: 'No company profile is linked yet. Try refreshing the page.' };
  if (!fields || Object.keys(fields).length === 0) {
    return { reason: 'no_fields', message: 'Nothing on this step to save yet. Fill in a few details and continue when ready.' };
  }
  return null;
}

export function notificationForSaveResult(result) {
  if (result.success) {
    if (result.strippedFields?.length > 0) {
      return {
        type: 'warning',
        heading: 'Progress saved',
        message: 'Most of your answers were saved. A couple of optional details could not be stored yet.',
      };
    }
    return {
      type: 'success',
      heading: 'Progress saved',
      message:
        result.fieldCount > 0
          ? 'Your answers have been added to your company profile.'
          : 'Your progress has been recorded.',
    };
  }

  if (result.skipped) {
    const isEarlyStep = result.reason === 'no_fields';
    return {
      type: 'warning',
      heading: isEarlyStep ? 'Nothing to save yet' : 'Could not save this step',
      message: result.message,
    };
  }

  return {
    type: 'warning',
    heading: 'This step did not fully save',
    message: result.message || 'Your answers are still here — try continuing again.',
  };
}

export { ONBOARDING_PROGRESS_FIELD } from './interestedOpportunities.js';

/**
 * Parse saved onboarding progress from companies.Onboarding.
 * Shape: { version, updatedAt, currentSectionIndex, currentSubsectionIndex,
 *          completedSubsections, completed, formData }
 */
export function parseOnboardingProgress(raw) {
  if (raw == null || raw === '') return null;

  let parsed = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{')) return null;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const formData =
    parsed.formData && typeof parsed.formData === 'object' && !Array.isArray(parsed.formData)
      ? parsed.formData
      : // Legacy: entire object was the form dump
        Object.fromEntries(
          Object.entries(parsed).filter(
            ([key]) =>
              ![
                'version',
                'updatedAt',
                'currentSectionIndex',
                'currentSubsectionIndex',
                'completedSubsections',
                'completed',
                'formData',
              ].includes(key)
          )
        );

  const completedSubsections = Array.isArray(parsed.completedSubsections)
    ? parsed.completedSubsections.map(String).filter(Boolean)
    : [];

  return {
    version: parsed.version || 1,
    updatedAt: parsed.updatedAt || null,
    currentSectionIndex: Number.isFinite(Number(parsed.currentSectionIndex))
      ? Math.max(0, Number(parsed.currentSectionIndex))
      : 0,
    currentSubsectionIndex: Number.isFinite(Number(parsed.currentSubsectionIndex))
      ? Math.max(0, Number(parsed.currentSubsectionIndex))
      : 0,
    completedSubsections,
    completed: Boolean(parsed.completed),
    formData,
  };
}

/** Serialize onboarding progress for companies.Onboarding. */
export function serializeOnboardingProgress({
  formData = {},
  currentSectionIndex = 0,
  currentSubsectionIndex = 0,
  completedSubsections = [],
  completed = false,
} = {}) {
  const cleanedForm =
    formData && typeof formData === 'object' && !Array.isArray(formData)
      ? Object.fromEntries(
          Object.entries(formData).filter(
            ([key, value]) =>
              key !== '__onboardingProgress' && value !== undefined
          )
        )
      : {};

  return JSON.stringify({
    version: 1,
    updatedAt: new Date().toISOString(),
    currentSectionIndex: Math.max(0, Number(currentSectionIndex) || 0),
    currentSubsectionIndex: Math.max(0, Number(currentSubsectionIndex) || 0),
    completedSubsections: Array.isArray(completedSubsections)
      ? [...new Set(completedSubsections.map(String).filter(Boolean))]
      : [],
    completed: Boolean(completed),
    formData: cleanedForm,
  });
}
